/** Client-side PostHog instance, populated by <Analytics /> after init. */
import { browser } from "$app/environment";

import type { PostHog } from "posthog-js/dist/module.no-external";

import { isObjectId } from "$lib/core/object-info";
import type { User } from "$lib/identity/user";

let _posthog: PostHog | undefined;
const _posthogAsyncQueue: { [key: number]: (posthog: PostHog) => void } = {};
let _queueTicker = 0;

export function setPosthog(instance: PostHog): void {
  _posthog = instance;
  Object.values(_posthogAsyncQueue).forEach((callback) => {
    callback(instance);
  });
}

export function getPosthog(): PostHog | undefined {
  return _posthog;
}

/**
 * Since we load asynchronously, we want some way to queue up logic.
 * This function sets up a given callback, and returns an unsubscribe method.
 */
export function onPosthog(callback: (posthog: PostHog) => void) {
  if (_posthog) {
    callback(_posthog);
    return;
  }

  const id = _queueTicker++;
  _posthogAsyncQueue[id] = callback;
  return () => {
    delete _posthogAsyncQueue[id];
  };
}

// Tab-scoped so a concurrent sign-in in another tab can't clear or overwrite
// an in-flight signup's marker; sessionStorage survives the same-tab OAuth
// redirect.
const SIGNUP_INVITED_KEY = "signup-invited";

export function setSignupInvited(invited: boolean) {
  if (!browser) return;
  sessionStorage.setItem(SIGNUP_INVITED_KEY, invited ? "true" : "false");
}

export function clearSignupInvited() {
  if (!browser) return;
  sessionStorage.removeItem(SIGNUP_INVITED_KEY);
}

/**
 * Associate the current device with the logged-in user. Safe to call on
 * every load: PostHog treats identify as a no-op when the distinct ID
 * already matches.
 */
export function identifyUser(user?: User): void {
  if (!browser) return;
  if (!user?.loggedIn) return;

  const personProperties: Record<string, string | boolean> = {
    email: user.notificationEmail,
    name: user.name,
    username: user.username,
  };
  const signupInvited = sessionStorage.getItem(SIGNUP_INVITED_KEY);
  if (signupInvited !== null) {
    personProperties.isInvited = signupInvited === "true";
    sessionStorage.removeItem(SIGNUP_INVITED_KEY);
  }

  getPosthog()?.identify(user.userId, personProperties);
}

const WORKSPACE_GROUP_TYPE = "workspace";

export function setUserFlagTargeting(user?: User): void {
  if (!browser) return;

  const posthog = getPosthog();
  if (!posthog) return;
  if (user?.loggedIn) {
    posthog.setPersonPropertiesForFlags({
      user_id: user.userId,
      username: user.username,
      is_admin: user.isAdmin ? "true" : "false",
    });
  } else {
    posthog.featureFlags.unsetPersonPropertiesForFlags(
      ["user_id", "username"],
      false,
    );
    posthog.setPersonPropertiesForFlags({ is_admin: "false" });
  }
}

export function setWorkspaceFlagTargeting(
  workspaceId: string,
  properties: { name?: string; plan?: string } = {},
): void {
  if (!browser) return;

  const posthog = getPosthog();
  if (!posthog) return;
  posthog.setPersonPropertiesForFlags({ workspace_id: workspaceId });
  posthog.group(WORKSPACE_GROUP_TYPE, workspaceId, {
    workspace_id: workspaceId,
    ...properties,
  });
}

export function clearWorkspaceFlagTargeting(): void {
  if (!browser) return;

  const posthog = getPosthog();
  if (!posthog) return;
  posthog.featureFlags.unsetPersonPropertiesForFlags(["workspace_id"], false);
  posthog.resetGroupPropertiesForFlags(WORKSPACE_GROUP_TYPE);
  posthog.resetGroups();
}

const replaceModalID = (id: string) => id.split("-")[0] + "-XXXX";

export type PostHogNamespace =
  | "command_palette"
  | "settings"
  | "apps"
  | "environments"
  | "endpoints"
  | "contact_form"
  | "signup"
  | "audit_logs"
  | "app_logs"
  | "coding_agent_demo";

/**
 * Capture a PostHog event, optionally stripping Modal IDs from property values
 * to avoid high-cardinality data. Avoid high cardinality so we can see what id
 * types users are interested in for future support.
 *
 * @param event - The event name to capture.
 * @param properties - Optional key-value properties sent with the event.
 *   Must include a `namespace` to scope the event.
 * @param options - Controls post-processing of properties before capture.
 *   `stripModalIDs` (default `true`) replaces Modal ID values with a redacted placeholder.
 */
export function trackEvent(
  event: string,
  properties?: { namespace: PostHogNamespace } & Record<string, any>,
  options: { stripModalIDs?: boolean } = { stripModalIDs: true },
) {
  if (options.stripModalIDs && properties) {
    for (const [key, value] of Object.entries(properties)) {
      if (isObjectId(value)) {
        properties[key] = replaceModalID(value);
      }
    }
  }

  getPosthog()?.capture(event, properties);
}
